import {
  FlatMetaPartySection,
  Party,
  ReservationsMetaSection,
  WalkInsMetaSection,
} from 'types/service';
import { NO_SEATING_AREA_TEXT } from 'utils/seatingAreaUtils';
import { PartyListSections } from 'constants/service/singlePartyList';
import { core } from '@tock/coretypes';
import { createSelector } from 'reselect';
import { featuresSelector, flexSelector } from 'selectors/featuresSelector';
import {
  filterParties,
  formatPartyListSection,
  getPartyListTextFilterSelector,
  selectedPartyListFilterValuesSelector,
  sortPartiesByTime,
} from 'selectors/service/partyListSelector';
import {
  guestListShowLastnameFirstNameSelector,
  isGuestListSeparateTabsSelector,
} from 'selectors/dashboardPreferenceSelectors';
import { isDiningVariety } from '@tock/shared/utils/offerings';
import {
  isPartyCanceled,
  isPartyExpected,
  isPartyFinished,
  isPartyNoShow,
  isPartyQuotedOriginalWalkIn,
  isPartySeated,
} from 'utils/partyUtils';
import { oc } from 'ts-optchain';
import {
  selectedGuestListSectionSelector,
  walkInSortSelector,
} from 'selectors/service/serviceUiSelector';
import { singleNotifyListSelector } from './singleNotifyListSelector';
import _ from 'lodash';
import currentBusinessSelector from 'selectors/service/currentBusinessSelector';
import partiesSelector from 'selectors/service/partiesSelector';

interface PartiesToSectionMap {
  [key: string]: Party[];
}

export interface PartyListMap {
  [key: string]: ReservationsMetaSection | WalkInsMetaSection;
}

export interface PartiesToSectionCountMap {
  [key: string]: number;
}

// Utility to append the item type to a party
const _buildPartyItems = (parties: Party[]): Party[] =>
  parties.map(({ listItemType, ...party }) => ({
    listItemType: 'Party',
    ...party,
  }));

// Sort parties in to an array per section
const _groupPartiesBySection = (
  parties: Party[],
  selectedGuestListTab: 'guestList' | 'notifyList' | 'orderList'
): PartiesToSectionMap => {
  if (selectedGuestListTab === 'orderList') {
    const initialSectionMap = {
      [PartyListSections.UPCOMING]: [],
      [PartyListSections.FINISHED]: [],
      [PartyListSections.CANCELLATIONS]: [],
      [PartyListSections.NO_SHOWS]: [],
    };
    return parties.reduce<PartiesToSectionMap>((sectionMap, party) => {
      const sectionKey = Object.keys(sectionMap).find((section) => {
        switch (section) {
          case PartyListSections.UPCOMING:
            return isPartyExpected(party) && !party.visit.delivered;

          // A finished togo/delivery experience is one that has been marked delivered of one that has been auto-closed (aka finished)
          case PartyListSections.FINISHED:
            return (isPartyExpected(party) && party.visit.delivered) || isPartyFinished(party);

          case PartyListSections.NO_SHOWS:
            return isPartyNoShow(party);

          case PartyListSections.CANCELLATIONS:
            return isPartyCanceled(party);

          default:
            return false;
        }
      });

      if (sectionKey && sectionMap[sectionKey]) {
        sectionMap[sectionKey].push(party);
      }
      return sectionMap;
    }, initialSectionMap);
  }
  const initialSectionMap = {
    [PartyListSections.WALKINS]: [],
    [PartyListSections.UPCOMING]: [],
    [PartyListSections.SEATED]: [],
    [PartyListSections.FINISHED]: [],
    [PartyListSections.CANCELLATIONS]: [],
    [PartyListSections.NO_SHOWS]: [],
  };

  return parties.reduce<PartiesToSectionMap>((sectionMap, party) => {
    const sectionKey = Object.keys(sectionMap).find((section) => {
      switch (section) {
        case PartyListSections.WALKINS:
          return isPartyQuotedOriginalWalkIn(party) && isPartyExpected(party);

        case PartyListSections.UPCOMING:
          return isPartyExpected(party) && !isPartyQuotedOriginalWalkIn(party);

        case PartyListSections.SEATED:
          return isPartySeated(party);

        case PartyListSections.FINISHED:
          return isPartyFinished(party);

        case PartyListSections.NO_SHOWS:
          return isPartyNoShow(party);

        case PartyListSections.CANCELLATIONS:
          return isPartyCanceled(party);

        default:
          return false;
      }
    });

    if (sectionKey && sectionMap[sectionKey]) {
      sectionMap[sectionKey].push(party);
    }
    return sectionMap;
  }, initialSectionMap);
};

// Convenience selector for raw party data that is then manipulated for display in the UI
export const filteredAndGroupedParties = createSelector(
  partiesSelector,
  getPartyListTextFilterSelector,
  selectedPartyListFilterValuesSelector,
  (parties, textFilter, selectedPartyListFilters) => {
    // Filter out irrelevant parties based on text search or filter menus
    const filteredParties = filterParties(parties, selectedPartyListFilters, textFilter);

    // Organize filtered data in to a map based on the party status
    return _groupPartiesBySection(filteredParties, selectedPartyListFilters.selectedGuestListTab);
  }
);

export const partyHasUnassignedWarning = (party: Party) =>
  party.state === 'EXPECTED' &&
  isDiningVariety(party.purchase?.ticketType?.variety) &&
  party.tables?.length === 0;

// Finds expected dining parties that don't have a seating assignment, whether intentional or not.
export const unassignedPartiesSelector = createSelector(partiesSelector, (parties) => {
  return parties.filter(partyHasUnassignedWarning);
});

// exported for testing
export const singlePartyListSelector = createSelector(
  filteredAndGroupedParties,
  flexSelector,
  walkInSortSelector,
  currentBusinessSelector,
  featuresSelector,
  guestListShowLastnameFirstNameSelector,
  (
    groupedParties,
    isFlex,
    walkInSortByArrival,
    currentBusiness,
    features,
    isGuestShowLastnameFirstnameEnabled
  ): PartyListMap => {
    // Format each section to match guest list data structure
    const timeZone = oc(currentBusiness).timeZone('US/Central');

    const featureForFirstNameSorting: core.Feature.FeatureName = isFlex
      ? 'PARTY_CARD_REDESIGN'
      : 'PARTY_CARD_REDESIGN_SLOT';

    const sortedAndGroupedParties = Object.keys(groupedParties).reduce<PartyListMap>(
      (map, section) => {
        const sortedParties = sortPartiesByTime(
          groupedParties[section],
          timeZone,
          walkInSortByArrival,
          !isGuestShowLastnameFirstnameEnabled && (features[featureForFirstNameSorting] ?? false),
          features.SHOW_DELIVERY_ORDER_AT_PICKUP_TIME,
          features.USE_ADJUSTED_VISIT_TIMES
        );

        // Walk ins are currently formatted differently and only have one heading level
        const partiesToFormat =
          section === PartyListSections.WALKINS && _.isArray(sortedParties)
            ? _.head(sortedParties) || {}
            : sortedParties;

        map[section] = formatPartyListSection(
          partiesToFormat,
          section,
          groupedParties[section].length
        );
        return map;
      },
      {}
    );

    return sortedAndGroupedParties;
  }
);

export const activePartyListSeatingOptions = createSelector(partiesSelector, (parties) => {
  const result = parties.reduce((acc, { purchase }) => {
    if (purchase) {
      if (purchase?.seatingAreaName) {
        acc.push({
          key: purchase.seatingAreaName,
          label: purchase.seatingAreaName,
          value: purchase.seatingAreaEnumValue,
        });
      } else {
        acc.push({
          key: NO_SEATING_AREA_TEXT,
          label: NO_SEATING_AREA_TEXT,
          value: NO_SEATING_AREA_TEXT,
        });
      }
    }
    return acc;
  }, [] as Array<StringSelectOption>);

  return _.uniqBy(result, (element) => element.key).sort((a, b) => a.label.localeCompare(b.label));
});

export const flattenedSinglePartyListSelector = createSelector(
  singlePartyListSelector,
  isGuestListSeparateTabsSelector,
  selectedGuestListSectionSelector,
  getPartyListTextFilterSelector,
  singleNotifyListSelector,
  (partyList: PartyListMap, isGuestListTabbed, selectedCategory, textFilter, waitlist) => {
    // If the list is tabbed and no search has been applied we return only the selected category
    let partiesToDisplay = partyList;

    if (!textFilter && isGuestListTabbed) {
      partiesToDisplay = { [selectedCategory]: partyList[selectedCategory] };
      if (selectedCategory === PartyListSections.CANCELLATIONS) {
        partiesToDisplay = {
          [PartyListSections.CANCELLATIONS]: partyList[PartyListSections.CANCELLATIONS],
          [PartyListSections.NO_SHOWS]: partyList[PartyListSections.NO_SHOWS],
        };
      }
    }

    // TODO (lframnes) we should consider adding the listItemType when the raw data is parsed
    // then flattening would not require knowlege of the hierarchy
    const results = Object.keys(partiesToDisplay).reduce<FlatMetaPartySection>((arr, metaKey) => {
      const meta = partiesToDisplay[metaKey];
      if (!meta) {
        return arr;
      }
      arr.push({ listItemType: 'metaSectionHeader', section: meta });

      // Walkins are a single section, we just add the parties
      if (meta.isWalkIns) {
        const m = meta as WalkInsMetaSection;
        return m.section.parties ? arr.concat(_buildPartyItems(m.section.parties)) : arr;
      } else {
        // Other categories are grouped by time and have section headers
        const m = meta as ReservationsMetaSection;
        return arr.concat(
          m.sections.reduce<FlatMetaPartySection>((sectionsAndParties, section) => {
            sectionsAndParties.push({ listItemType: 'sectionHeader', ...section });
            return sectionsAndParties.concat(_buildPartyItems(section.parties));
          }, [])
        );
      }
    }, []);

    // If a search has been applied we add any matching waitlist data to the results
    if (textFilter) {
      return results.concat(waitlist);
    }

    return results;
  }
);

/**
 * Return an flattened array of all parties in a single guest list
 */
export const flatSinglePartiesList = createSelector(
  singlePartyListSelector,
  isGuestListSeparateTabsSelector,
  selectedGuestListSectionSelector,
  getPartyListTextFilterSelector,
  (partyList: PartyListMap, isGuestListTabbed, selectedCategory, textFilter): Party[] => {
    // If the list is tabbed and no search has been applied we return only the selected category
    let categoriesToDisplay = Object.keys(partyList);

    if (!textFilter && isGuestListTabbed) {
      categoriesToDisplay = [selectedCategory];
      if (selectedCategory === PartyListSections.CANCELLATIONS) {
        categoriesToDisplay = [PartyListSections.CANCELLATIONS, PartyListSections.NO_SHOWS];
      }
    }
    if (!categoriesToDisplay.length) {
      return [];
    }
    return categoriesToDisplay.reduce<Party[]>((arr, key) => {
      const partyCategory = partyList[key];
      if (!partyCategory) {
        return arr;
      }

      const groups = partyCategory.isWalkIns
        ? [(partyCategory as WalkInsMetaSection).section]
        : (partyCategory as ReservationsMetaSection).sections;

      const parties = _.flatMap(groups, (g) => (g.parties ? g.parties : []));
      return parties ? arr.concat(parties) : arr;
    }, []);
  }
);

export const singlePartyListSummary = createSelector(filteredAndGroupedParties, (groupedParties) =>
  Object.keys(groupedParties).reduce<PartiesToSectionCountMap>((obj, partyKey) => {
    obj[partyKey] = groupedParties[partyKey].length;

    // Cancellations are the combined total of cancelled and no show sections
    if (partyKey === PartyListSections.CANCELLATIONS) {
      obj[partyKey] += groupedParties[PartyListSections.NO_SHOWS].length;
    }

    return obj;
  }, {})
);
